import React, { useState } from 'react';
import { useScreenInit } from '../useScreenInit.js';
import { Shield, Clock, ChevronLeft, ChevronRight, CheckCircle2, Cloud, Lock, Star, HelpCircle, X } from 'lucide-react';
import { ROLES } from '../data/framework';
import { ITEM_TYPE_LABEL, standardSelection, selectedInDomain } from '../utils/framework';
import type { ItemType } from '../types/framework';
import { personById } from '../data/demoOrganisation';
const SUBJECT = personById('p2');
type RatingType = '5-point' | '10-point' | 'multiple-choice' | 'open-text';
type Question = {
  id: string;
  text: string;
  competency: string;
  itemType: ItemType;
  type: RatingType;
  options?: string[];
};

// The questionnaire a subscriber built for this cycle: one page per
// competency domain, 2 Characteristic + 2 Behaviour statements each.
const CYCLE_ROLE = ROLES[0];
const CYCLE_SELECTION = standardSelection(CYCLE_ROLE);
const QUESTION_PAGES: {
  competency: string;
  questions: Question[];
}[] = CYCLE_ROLE.domains.map(domain => ({
  competency: domain.name,
  questions: selectedInDomain(domain, CYCLE_SELECTION).map(item => ({
    id: `${CYCLE_ROLE.id}-${item.no}`,
    text: item.criterion,
    competency: item.competency,
    itemType: item.type,
    type: '5-point' as const
  }))
}));
const PAGE_OFFSETS = QUESTION_PAGES.map((_, i) => QUESTION_PAGES.slice(0, i).reduce((n, p) => n + p.questions.length, 0));
const SCALE_5 = ['Strongly Disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly Agree'];
// Step model: 0 Welcome, 1 Instructions, 2..(2+pages-1) Questions, last Completion
const TOTAL_QUESTION_PAGES = QUESTION_PAGES.length;
const FIRST_QUESTION_STEP = 2;
const COMPLETION_STEP = FIRST_QUESTION_STEP + TOTAL_QUESTION_PAGES;
function QuestionInput({
  q,
  value,
  onChange
}: {
  q: Question;
  value: any;
  onChange: (v: any) => void;
}) {
  if (q.type === '5-point') {
    return <div className="grid grid-cols-5 gap-2" data-id="element-465" data-mp="3fixascef6nwb">
        {SCALE_5.map((label, i) => {
        const n = i + 1;
        const selected = value === n;
        return <button key={n} onClick={() => onChange(n)} className={`flex flex-col items-center justify-start gap-2 p-3 rounded-lg border text-center transition-all ${selected ? 'border-slate-900 bg-slate-50 ring-1 ring-slate-900' : 'border-slate-200 hover:border-slate-300'}`} data-id="element-466" data-mp="1aiw7prf3st7l" data-mpi={i}>
              <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${selected ? 'bg-slate-900 text-white' : 'bg-white border border-slate-300 text-slate-600'}`} data-id="element-467" data-mp="wsakjqim9x3w">
                {n}
              </span>
              <span className="text-[11px] leading-tight text-slate-500" data-id="element-468" data-mp="10sb2hl4xcqfp">
                {label}
              </span>
            </button>;
      })}
      </div>;
  }
  if (q.type === '10-point') {
    return <div className="flex flex-wrap gap-2" data-id="element-469" data-mp="90lmrun10gfk">
        {Array.from({
        length: 10
      }, (_, i) => i + 1).map(n => {
        const selected = value === n;
        return <button key={n} onClick={() => onChange(n)} className={`w-10 h-10 rounded-full border text-sm font-medium transition-all ${selected ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-300 bg-white text-slate-600 hover:border-slate-400'}`} data-id="element-470" data-mp="3prfah8ogh1r">
              {n}
            </button>;
      })}
      </div>;
  }
  if (q.type === 'multiple-choice') {
    return <div className="space-y-2" data-id="element-471" data-mp="7qjq3ex76u05">
        {(q.options || []).map(opt => {
        const selected = value === opt;
        return <button key={opt} onClick={() => onChange(opt)} className={`w-full flex items-center gap-3 p-3 rounded-lg border text-left text-sm font-medium transition-all ${selected ? 'border-slate-900 bg-slate-50 ring-1 ring-slate-900' : 'border-slate-200 hover:border-slate-300'}`} data-id="element-472" data-mp="cnjuzmib9qpo">
              <span className={`w-4 h-4 rounded-full border flex items-center justify-center ${selected ? 'border-slate-900' : 'border-slate-300'}`} data-id="element-473" data-mp="38vrwsq1yoj7q">
                {selected && <span className="w-2 h-2 rounded-full bg-slate-900" data-id="element-474" data-mp="2yy42u2c6jfqm" />}
              </span>
              {opt}
            </button>;
      })}
      </div>;
  }
  return <textarea rows={4} value={value || ''} onChange={e => onChange(e.target.value)} placeholder="Share your feedback…" className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-slate-900" data-id="element-475" data-mp="3rlq8dg4ywug4" />;
}
export function AssessmentRunner() {
  const initial = useScreenInit();
  const [step, setStep] = useState<number>(typeof initial.step === 'number' ? initial.step : 0);
  const [anonymous, setAnonymous] = useState<boolean>(initial.anonymous !== false);
  const [answers, setAnswers] = useState<Record<string, any>>({});
  const [showHelp, setShowHelp] = useState(false);
  const isWelcome = step === 0;
  const isInstructions = step === 1;
  const isCompletion = step >= COMPLETION_STEP;
  const isQuestion = !isWelcome && !isInstructions && !isCompletion;
  const questionPageIndex = step - FIRST_QUESTION_STEP;
  const currentPage = isQuestion ? QUESTION_PAGES[questionPageIndex] : null;
  const answeredCount = Object.keys(answers).length;
  const totalQuestions = QUESTION_PAGES.reduce((n, p) => n + p.questions.length, 0);
  const progressPct = Math.min(100, Math.round(answeredCount / totalQuestions * 100));
  const next = () => setStep(s => Math.min(COMPLETION_STEP, s + 1));
  const back = () => setStep(s => Math.max(0, s - 1));
  const AnonBadge = () => <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-violet-50 text-violet-700 border border-violet-100" data-id="element-476" data-mp="3l57xws0a0ij9">
      <Shield className="h-3.5 w-3.5 mr-1.5" data-id="element-477" data-mp="3d7ed8b7gendx" /> Anonymous
    </span>;
  return <div className="min-h-screen bg-slate-50 flex flex-col font-sans" data-id="element-478" data-mp="395b0zkcynerl">
      {/* Top bar */}
      <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 sm:px-6 sticky top-0 z-20" data-id="element-479" data-mp="3n52hbrqy978r">
        <div className="flex items-center" data-id="element-480" data-mp="3i6r5xcl0aoof">
          <div className="w-6 h-6 bg-slate-900 rounded-sm mr-3" data-id="element-481" data-mp="3w34g00n7nnsv" />
          <span className="font-semibold tracking-tight text-slate-900" data-id="element-482" data-mp="3s2u98lrlgtcy">
            Oversight
          </span>
        </div>
        <div className="flex items-center gap-3" data-id="element-483" data-mp="9out5vyxinu7">
          {anonymous && <AnonBadge data-id="element-484" data-mp="4pi5xa45ber3" />}
          {isQuestion && <span className="hidden sm:inline-flex items-center text-xs font-medium text-slate-400" data-id="element-485" data-mp="4qt8j6luuggm">
              <Cloud className="h-3.5 w-3.5 mr-1.5 text-emerald-500" data-id="element-486" data-mp="jnak9yxyhx3p" /> Saved
            </span>}
          <button onClick={() => setShowHelp(true)} className="text-slate-400 hover:text-slate-600" title="Help" data-id="element-487" data-mp="ep935jk3c7uv">
            <HelpCircle className="h-5 w-5" data-id="element-488" data-mp="12bcou8nh89fy" />
          </button>
        </div>
      </header>

      {/* Progress bar (questions only) */}
      {isQuestion && <div className="bg-white border-b border-slate-200 px-4 sm:px-6 py-3" data-id="element-489" data-mp="odo2sxhoaw9b">
          <div className="max-w-2xl mx-auto" data-id="element-490" data-mp="t8jx64adtvpl">
            <div className="flex items-center justify-between text-xs font-medium text-slate-500 mb-2" data-id="element-491" data-mp="ekyugbhf5yi7">
              <span data-id="element-492" data-mp="jcqowx3p4odm">
                {currentPage?.competency} · Section {questionPageIndex + 1} of{' '}
                {TOTAL_QUESTION_PAGES}
              </span>
              <span data-id="element-493" data-mp="5izapbf0sgbk">{progressPct}% complete</span>
            </div>
            <div className="w-full bg-slate-100 rounded-full h-2" data-id="element-494" data-mp="akexb7wuiuw2">
              <div className="bg-slate-900 h-2 rounded-full transition-all duration-300" style={{
            width: `${progressPct}%`
          }} data-id="element-495" data-mp="3rrqt7vgaui55" />
            </div>
          </div>
        </div>}

      {/* Body */}
      <main className="flex-1 flex flex-col px-4 sm:px-6 py-8 sm:py-12" data-id="element-496" data-mp="c2exovwug6zt">
        <div className="w-full max-w-2xl mx-auto flex-1" data-id="element-497" data-mp="c011ukucaqr7">
          {/* WELCOME */}
          {isWelcome && <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-8 sm:p-10 text-center animate-in fade-in duration-300" data-id="element-498" data-mp="23y3lq2lzc9j">
              <div className="mx-auto w-14 h-14 bg-slate-900 rounded-xl flex items-center justify-center mb-6" data-id="element-499" data-mp="3tbake3zkxnox">
                <Star className="h-7 w-7 text-white" data-id="element-500" data-mp="3oiqzelykbqa3" />
              </div>
              <p className="text-sm font-medium text-slate-500 mb-2" data-id="element-501" data-mp="3kh5p98dexq3l">
                You've been invited to provide feedback
              </p>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3" data-id="element-502" data-mp="3fkoxm4708tlr">
                2026 {CYCLE_ROLE.name} Review
              </h1>
              <p className="text-slate-500 mb-6" data-id="element-503" data-mp="35k6an0k58wzm">
                You're providing feedback for{' '}
                <strong className="text-slate-700" data-id="element-504" data-mp="2vh9e66nma71t">{SUBJECT.name}</strong> in the
                role of{' '}
                <strong className="text-slate-700" data-id="element-505" data-mp="30et07sqk3o4b">{CYCLE_ROLE.name}</strong>,
                across {TOTAL_QUESTION_PAGES} competency domains.
              </p>
              <div className="flex flex-wrap items-center justify-center gap-3 mb-8 text-sm" data-id="element-506" data-mp="3ae0c0bhn7apb">
                <span className="inline-flex items-center px-3 py-1.5 rounded-full bg-slate-100 text-slate-600 font-medium" data-id="element-507" data-mp="3abnufzfsr2cs">
                  <Clock className="h-4 w-4 mr-1.5" data-id="element-508" data-mp="2hotglldhj18a" /> ~
                  {Math.ceil(totalQuestions / 4)} minutes
                </span>
                <span className="inline-flex items-center px-3 py-1.5 rounded-full bg-slate-100 text-slate-600 font-medium" data-id="element-509" data-mp="2mmuyi2z6cui6">
                  {totalQuestions} questions
                </span>
                {anonymous && <AnonBadge data-id="element-510" data-mp="1tziadodd8emr" />}
              </div>

              <label className="flex items-center justify-center gap-2 text-sm text-slate-600 mb-8 cursor-pointer" data-id="element-511" data-mp="1yybp1k4e2boi">
                <input type="checkbox" checked={anonymous} onChange={e => setAnonymous(e.target.checked)} className="h-4 w-4 text-slate-900 focus:ring-slate-900 border-slate-300 rounded" data-id="element-512" data-mp="3qqpvxsnhtqtg" />
                Submit my responses anonymously
              </label>

              <button onClick={next} className="w-full sm:w-auto bg-slate-900 text-white px-8 py-3 rounded-md text-sm font-medium hover:bg-slate-800 transition-colors inline-flex items-center justify-center" data-id="element-513" data-mp="8i7br53us5rm">
                Get Started <ChevronRight className="h-4 w-4 ml-1" data-id="element-514" data-mp="3gzbpt211dsrc" />
              </button>
            </div>}

          {/* INSTRUCTIONS */}
          {isInstructions && <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-8 sm:p-10 animate-in fade-in duration-300" data-id="element-515" data-mp="2p9qqgsy84a8d">
              <h1 className="text-2xl font-bold text-slate-900 mb-2" data-id="element-516" data-mp="2t8zhhq1vpu08">
                Before you begin
              </h1>
              <p className="text-slate-500 mb-8" data-id="element-517" data-mp="2lox8dfsu062j">
                A few things to keep in mind as you complete this assessment.
              </p>

              <div className="space-y-5 mb-8" data-id="element-518" data-mp="2cpjxqjsz1bg8">
                <div className="flex gap-4" data-id="element-519" data-mp="2csfy0esakc7u">
                  <div className="bg-slate-100 text-slate-700 w-8 h-8 rounded-lg flex items-center justify-center shrink-0 font-medium text-sm" data-id="element-520" data-mp="27s1nmq37wq6n">
                    1
                  </div>
                  <div data-id="element-521" data-mp="1y0x9ttpa3bde">
                    <h3 className="text-sm font-semibold text-slate-900" data-id="element-522" data-mp="1y0dzay1yjp9e">
                      Rate honestly
                    </h3>
                    <p className="text-sm text-slate-500 mt-0.5" data-id="element-523" data-mp="1t09ljfsfp8mv">
                      Use the full range of the scale. There are no right or
                      wrong answers.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4" data-id="element-524" data-mp="1f2b6072dn65s">
                  <div className="bg-slate-100 text-slate-700 w-8 h-8 rounded-lg flex items-center justify-center shrink-0 font-medium text-sm" data-id="element-525" data-mp="1f4nyvf9ob0xm">
                    2
                  </div>
                  <div data-id="element-526" data-mp="1afd4igvw9q48">
                    <h3 className="text-sm font-semibold text-slate-900" data-id="element-527" data-mp="1abpdbodx9fso">
                      Understand the scale
                    </h3>
                    <p className="text-sm text-slate-500 mt-0.5" data-id="element-528" data-mp="10gxcmbp1mcto">
                      Each statement uses a 5-point scale from{' '}
                      <strong data-id="element-529" data-mp="1o68fbypyehag">Strongly Disagree</strong> to{' '}
                      <strong data-id="element-530" data-mp="1t3i2t8ts8ivn">Strongly Agree</strong>. Every domain has two
                      Characteristic and two Behaviour statements.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4" data-id="element-531" data-mp="1jdfcjge1pj10">
                  <div className="bg-slate-100 text-slate-700 w-8 h-8 rounded-lg flex items-center justify-center shrink-0 font-medium text-sm" data-id="element-532" data-mp="1jcdsimf1xm75">
                    3
                  </div>
                  <div data-id="element-533" data-mp="2bwvsayeq7x3v">
                    <h3 className="text-sm font-semibold text-slate-900" data-id="element-534" data-mp="fskqxtk1x3b">
                      Your progress is saved
                    </h3>
                    <p className="text-sm text-slate-500 mt-0.5" data-id="element-535" data-mp="3rlty5d0zt2s4">
                      You can leave and return anytime — your answers are saved
                      automatically.
                    </p>
                  </div>
                </div>
              </div>

              <div className={`flex items-start gap-3 rounded-lg p-4 mb-8 border ${anonymous ? 'bg-violet-50 border-violet-100' : 'bg-slate-50 border-slate-200'}`} data-id="element-536" data-mp="3dlsnj5uxegzg">
                <Lock className={`h-5 w-5 shrink-0 mt-0.5 ${anonymous ? 'text-violet-600' : 'text-slate-500'}`} data-id="element-537" data-mp="3mniob2ap6s9w" />
                <p className="text-sm text-slate-600" data-id="element-538" data-mp="3inrragukvfwx">
                  {anonymous ? 'This assessment is anonymous. Your individual responses will be combined with others and the subject will never see who said what.' : 'Your name will be visible with your responses to the assessment administrator.'}
                </p>
              </div>

              <div className="flex items-center justify-between" data-id="element-539" data-mp="1x7q2ra2en1fi">
                <button onClick={back} className="text-sm font-medium text-slate-600 hover:text-slate-900 inline-flex items-center" data-id="element-540" data-mp="2261ddn0ixfys">
                  <ChevronLeft className="h-4 w-4 mr-1" data-id="element-541" data-mp="2c50duib4y5ap" /> Back
                </button>
                <button onClick={next} className="bg-slate-900 text-white px-6 py-2.5 rounded-md text-sm font-medium hover:bg-slate-800 transition-colors inline-flex items-center" data-id="element-542" data-mp="2g62biupem9ak">
                  Begin Assessment <ChevronRight className="h-4 w-4 ml-1" data-id="element-543" data-mp="2pvdlsko11k01" />
                </button>
              </div>
            </div>}

          {/* QUESTION PAGES */}
          {isQuestion && currentPage && <div className="animate-in fade-in duration-300 space-y-6" data-id="element-544" data-mp="2zub6fcic0tdr">
              {currentPage.questions.map((q, i) => <div key={q.id} className="bg-white border border-slate-200 rounded-2xl shadow-sm p-6 sm:p-8" data-id="element-545" data-mp="j7mo1o2vlm86" data-mpi={i}>
                  <div className="flex items-start gap-3 mb-5" data-id="element-546" data-mp="e91wse5lqcvv">
                    <span className="text-xs font-semibold text-slate-400 mt-1 shrink-0" data-id="element-547" data-mp="9a8kuq5yqtxt">
                      Q{PAGE_OFFSETS[questionPageIndex] + i + 1}
                    </span>
                    <div data-id="element-548" data-mp="11ye6rg8r6oz8">
                      <span className={`inline-block mb-2 px-2 py-0.5 rounded-full text-xs font-medium border ${q.itemType === 'characteristic' ? 'bg-indigo-50 text-indigo-700 border-indigo-100' : 'bg-teal-50 text-teal-700 border-teal-100'}`} data-id="element-549" data-mp="11z5phqh94s83">
                        {ITEM_TYPE_LABEL[q.itemType]}
                      </span>
                      <h2 className="text-base sm:text-lg font-medium text-slate-900" data-id="element-550" data-mp="1kx8i0f90d7an">
                        {q.text}
                      </h2>
                    </div>
                  </div>
                  <QuestionInput q={q} value={answers[q.id]} onChange={v => setAnswers(a => ({
              ...a,
              [q.id]: v
            }))} data-id="element-551" data-mp="3k3nmzkourozc" />
                </div>)}

              <div className="flex items-center justify-between pt-2" data-id="element-552" data-mp="2wcrracha09mt">
                <button onClick={back} className="px-4 py-2.5 border border-slate-300 rounded-md text-sm font-medium text-slate-700 hover:bg-white bg-white transition-colors inline-flex items-center" data-id="element-553" data-mp="30gzg09nqhpl1">
                  <ChevronLeft className="h-4 w-4 mr-1" data-id="element-554" data-mp="2rh2vmk8dncvs" /> Previous
                </button>
                <div className="flex items-center gap-3" data-id="element-555" data-mp="kmpgyu4lm9re">
                  <button className="hidden sm:inline text-sm font-medium text-slate-500 hover:text-slate-900" data-id="element-556" data-mp="pmtt67yh4ibt">
                    Save & exit
                  </button>
                  <button onClick={next} className="bg-slate-900 text-white px-6 py-2.5 rounded-md text-sm font-medium hover:bg-slate-800 transition-colors inline-flex items-center" data-id="element-557" data-mp="257cnxgij94e2">
                    {questionPageIndex === TOTAL_QUESTION_PAGES - 1 ? 'Submit' : 'Next'}
                    <ChevronRight className="h-4 w-4 ml-1" data-id="element-558" data-mp="2sy8cm4rozjh3" />
                  </button>
                </div>
              </div>
            </div>}

          {/* COMPLETION */}
          {isCompletion && <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-8 sm:p-12 text-center animate-in fade-in zoom-in-95 duration-500" data-id="element-559" data-mp="2xvrq32gkfs7z">
              <div className="mx-auto w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mb-6" data-id="element-560" data-mp="18h3lvoi4pgl1">
                <CheckCircle2 className="h-8 w-8 text-emerald-600" data-id="element-561" data-mp="13jk1s7yr29l6" />
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3" data-id="element-562" data-mp="ho1legdly6po">
                Thank you!
              </h1>
              <p className="text-slate-500 max-w-md mx-auto mb-6" data-id="element-563" data-mp="re4lf0j5ckxi">
                Your feedback for{' '}
                <strong className="text-slate-700" data-id="element-564" data-mp="wcy2t41k0w2y">{SUBJECT.name}</strong> has
                been submitted successfully.
                {anonymous && ' Your responses remain completely anonymous.'}
              </p>
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-left max-w-md mx-auto mb-8" data-id="element-565" data-mp="3nqsl7sqmi1v">
                <h3 className="text-sm font-semibold text-slate-900 mb-1" data-id="element-566" data-mp="8mkadcf24v7u">
                  What happens next?
                </h3>
                <p className="text-sm text-slate-500" data-id="element-567" data-mp="cn42u2oorann">
                  Once all respondents have completed their feedback, results
                  are aggregated into a confidential report and shared with the
                  subject and their manager.
                </p>
              </div>
              <button className="text-sm font-medium text-slate-600 hover:text-slate-900" data-id="element-568" data-mp="38oiqrx8htmv9">
                You may now close this window.
              </button>
            </div>}
        </div>
      </main>

      {/* Help modal */}
      {showHelp && <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-569" data-mp="2utyj2c9vclt0">
          <div className="absolute inset-0 bg-slate-900/30 animate-in fade-in" onClick={() => setShowHelp(false)} data-id="element-570" data-mp="2pwg8yik480jf" />
          <div className="relative w-full max-w-sm bg-white rounded-xl shadow-xl p-6 animate-in zoom-in-95 duration-200" data-id="element-571" data-mp="2kviry9audcji">
            <div className="flex items-center justify-between mb-3" data-id="element-572" data-mp="2bw5ez6nie5u1">
              <h2 className="text-base font-semibold text-slate-900" data-id="element-573" data-mp="2bxzyqhn1nrwj">
                Need help?
              </h2>
              <button onClick={() => setShowHelp(false)} className="text-slate-400 hover:text-slate-600" data-id="element-574" data-mp="f1j283amejzr">
                <X className="h-5 w-5" data-id="element-575" data-mp="13ebvts0oq4zz" />
              </button>
            </div>
            <p className="text-sm text-slate-500 mb-4" data-id="element-576" data-mp="yfqwdut9bpcl">
              Your progress is saved automatically. You can close this window
              and return via your invitation link at any time.
            </p>
            {anonymous && <div className="flex items-start gap-2 text-sm text-violet-700 bg-violet-50 border border-violet-100 rounded-md p-3" data-id="element-577" data-mp="oq79me2fbbcg">
                <Shield className="h-4 w-4 shrink-0 mt-0.5" data-id="element-578" data-mp="fpsopftdfy9h" /> Your responses
                are anonymous.
              </div>}
          </div>
        </div>}
    </div>;
}